import { ErrorCode } from '@gql-generated/graphql'
import type { GraphQLFormattedError } from 'graphql'
import type React from 'react'

/**
 * A generic error code to use when no specific error code applies.
 * This can be used in Drawer submit actions to trigger an error which is not a graphql error,
 * while still getting the handleErrors behavior. (drawer won't close, loading state will stop, etc.)
 */
export const ERROR_CODE_GENERIC = [
  {
    message: 'default',
    extensions: {
      code: ErrorCode.TestError, // A specific error code has to be returned for handleErrors to trigger Drawer.onActionFailed...
    },
  },
]

function isErrorCode(code: unknown): code is ErrorCode {
  if (code == ERROR_CODE_GENERIC) return true

  return typeof code === 'string' && (Object.values(ErrorCode) as string[]).includes(code)
}

type ErrorHandler = (error?: GraphQLFormattedError) => React.ReactNode | unknown

export type ErrorHandlers = Partial<Record<ErrorCode, ErrorHandler>> & {
  default?: ErrorHandler
}

export type ErrorHandlerMap = { onError: ErrorHandlers; onSuccess?: ErrorHandler }

/**
 * Processes GraphQL errors and handles them according to their error codes using provided error handlers.
 *
 * @param errors - An array of GraphQL formatted errors to process
 * @param onError - An object mapping error codes to handler functions
 * @param onSuccess - Optional callback when no matching error is found
 * @returns The result of the error handler function for the first matching error, or the success handler if no match
 *
 * @example
 * ```typescript
 * handleErrors(graphqlErrors, {
 *   onError: {
 *     [ErrorCode.NOT_FOUND]: (error) => <NotFoundMessage />,
 *     [ErrorCode.UNAUTHORIZED]: (error) => redirectToLogin(),
 *     [ErrorCode.ALREADY_ACTIVE]: (error) => Toast.error({ message: 'Account is already active' }), // Client component only
 *     [ErrorCode.SOME_ERROR]: (error) => console.error(`this error was thrown: ${error.message}`),
 *     default: (error) => <ErrorMessage message={error.message} />,
 *   },
 *   onSuccess: () => <SuccessMessage />,
 * })
 * ```
 */
export function handleErrors(
  errors: readonly GraphQLFormattedError[] | undefined,
  { onError, onSuccess }: ErrorHandlerMap,
) {
  if (!errors) return onSuccess?.()

  const errorList = Array.isArray(errors) ? errors : ERROR_CODE_GENERIC

  if (!errorList.length) return onSuccess?.()

  for (const error of errorList) {
    const code = error.extensions?.code

    if (isErrorCode(code)) {
      const handler = onError[code] ?? onError.default

      return handler?.(error)
    }
  }

  return onSuccess?.()
}
